import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useAuth } from '@zep/auth';
import { OperationType, PlanProduct, PlanType, SellingSpaceTemplateData, SpacePlanPaymentInfo, SubscribePlan } from '../../../../../types';
import { PLAN_PRICES_PER_CCU } from '../../../shared';
import { useAutoSelectedPlanInfo } from '../../hooks';
import { OnetimePeriod, PlanPaymentMethodType, PlanTemplateType, PriceLocale } from '../../types';
import { usePlanPaymentFormContext } from './PlanPaymentFormProvider';

/**
 * 전달받은 props를 통해 결제 페이지에서 사용할 정보를 제공하는 Provider
 *
 */
export const usePlanPaymentInfoContext = () => {
  const context = useContext(PlanPaymentInfoContext);
  if (!context) {
    throw new Error('usePlanPaymentInfoContext must be used within a PlanPaymentInfoProvider');
  }
  return context;
};
export const PlanPaymentInfoProvider = ({
  children,
  subscriptionPlanTemplates,
  onetimePlanTemplates,
  isKrPayment,
  spaceHashId,
  planPaymentInfo
}: PropsWithChildren<PlanPaymentInfoProviderProps>) => {
  const {
    user
  } = useAuth();
  // 결제페에지의 전역적인 로딩상태를 관리하기 위한 state
  const [isPlanPaymentLoading, setIsPlanPaymentLoading] = useState(false);
  const {
    control
  } = usePlanPaymentFormContext();
  const watchedValues = useWatch({
    control
  });
  const locale: PriceLocale = isKrPayment ? 'ko' : 'en';
  const prices = PLAN_PRICES_PER_CCU[locale];
  const isUpdate = planPaymentInfo?.spacePlan?.status === 'ACTIVE';
  const currentOperationType = isUpdate && planPaymentInfo?.spacePlan?.operationType ? planPaymentInfo?.spacePlan?.operationType : undefined;
  const currentOnetimePeriod = isUpdate && planPaymentInfo?.spacePlan?.onetimePeriod ? planPaymentInfo?.spacePlan?.onetimePeriod as OnetimePeriod : undefined;
  const currentPlanMaxCcu = isUpdate && planPaymentInfo?.spacePlan?.maxCcu ? planPaymentInfo?.spacePlan?.maxCcu : undefined;
  const currentPlanType = isUpdate && planPaymentInfo?.spacePlan?.planType ? planPaymentInfo?.spacePlan?.planType : 'FREE';
  const is일시납PRO사용중 = isUpdate && currentPlanType === 'PRO' && currentOperationType === OperationType.일시납;
  const currentSpacePlanHashId = isUpdate && planPaymentInfo?.spacePlan?.spacePlanHashId ? planPaymentInfo?.spacePlan?.spacePlanHashId : undefined;
  const selectedPlanInfo = useAutoSelectedPlanInfo({
    plans: watchedValues.planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION ? subscriptionPlanTemplates.planProducts : onetimePlanTemplates.planProducts,
    planType: watchedValues.planType as Omit<SubscribePlan, 'free'>,
    maxCcu: watchedValues.maxCcu as number,
    planTemplateType: watchedValues.planTemplateType as PlanTemplateType,
    subscriptionPeriod: watchedValues.subscriptionPeriod,
    onetimePeriod: watchedValues.onetimePeriod
  });
  const currencyString = isKrPayment ? '₩' : '$';
  return <PlanPaymentInfoContext.Provider value={{
    prices,
    spaceHashId,
    spaceName: planPaymentInfo?.space.name || '',
    isUpdate,
    isKrPayment,
    selectedPlanInfo,
    planPaymentInfo,
    currentOperationType,
    currentOnetimePeriod: currentOnetimePeriod as OnetimePeriod,
    isPlanPaymentLoading,
    setIsPlanPaymentLoading,
    currentPlanMaxCcu,
    currentPlanType,
    is일시납PRO사용중,
    currentSpacePlanHashId,
    currencyString,
    userCountryCode: user?.countryCode
  }} data-sentry-element="unknown" data-sentry-component="PlanPaymentInfoProvider" data-sentry-source-file="PlanPaymentInfoProvider.tsx">
      {children}
    </PlanPaymentInfoContext.Provider>;
};
const PlanPaymentInfoContext = createContext<PlanPaymentInfoContextType | undefined>(undefined);
export interface PlanPaymentInfoProviderProps {
  subscriptionPlanTemplates: SellingSpaceTemplateData;
  onetimePlanTemplates: SellingSpaceTemplateData;
  isKrPayment: boolean;
  spaceHashId: string;
  planPaymentInfo?: SpacePlanPaymentInfo;
}
type PlanPaymentInfoContextType = {
  prices: PlanPaymentMethodType;
  spaceHashId: string;
  spaceName: string;
  isUpdate: boolean;
  isKrPayment: boolean;
  selectedPlanInfo: PlanProduct;
  planPaymentInfo?: SpacePlanPaymentInfo;
  currentOperationType?: OperationType;
  currentOnetimePeriod?: OnetimePeriod;
  isPlanPaymentLoading: boolean;
  setIsPlanPaymentLoading: Dispatch<SetStateAction<boolean>>;
  currentPlanMaxCcu?: number;
  currentPlanType?: PlanType;
  is일시납PRO사용중: boolean;
  currentSpacePlanHashId?: string;
  currencyString: string;
  userCountryCode?: string | null;
};